import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
  Send,
  MessageSquare,
  Image,
  FileText,
  Users,
  User,
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Loader2,
  History,
  Zap,
  Search,
  Plus,
  Trash2,
  Save,
  Target,
  TrendingDown,
  UserX,
  Bookmark,
  X,
  Lock,
  ArrowLeft
} from "lucide-react";

export default function TeacherBroadcast() {
  const { teacher, school } = useOutletContext();
  const navigate = useNavigate();
  
  // Feature flag check for broadcast feature
  const { isEnabled: isBroadcastEnabled, isLoading: flagLoading } = useFeatureFlag(
    'b2b_teacher_broadcast',
    { 
      role: 'teacher',
      school_id: school?.id || school?.school_id 
    }
  );
  
  // Quota state
  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  
  // Compose state
  const [messageType, setMessageType] = useState("text");
  const [recipientType, setRecipientType] = useState("class");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [messageContent, setMessageContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  
  // Custom group filters
  const [filterGrades, setFilterGrades] = useState([]);
  const [filterMinAccuracy, setFilterMinAccuracy] = useState(null);
  const [filterMaxAccuracy, setFilterMaxAccuracy] = useState(null);
  const [filterInactiveDays, setFilterInactiveDays] = useState(null);
  const [savedGroups, setSavedGroups] = useState([]);
  const [selectedSavedGroup, setSelectedSavedGroup] = useState("");
  const [showSaveGroupDialog, setShowSaveGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  
  // Individual selection state
  const [allStudents, setAllStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentGradeFilter, setStudentGradeFilter] = useState("");
  const [studentSectionFilter, setStudentSectionFilter] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Data state
  const [classes, setClasses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState(null);
  const [history, setHistory] = useState([]);
  
  // UI state
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("compose");

  useEffect(() => {
    // Only fetch data if feature flag is enabled
    if (!flagLoading && isBroadcastEnabled) {
      fetchQuota();
      fetchClasses();
      fetchTemplates();
      fetchHistory();
      fetchSavedGroups();
    }
  }, [flagLoading, isBroadcastEnabled]);

  // Fetch students when individual selection is chosen
  useEffect(() => {
    if (recipientType === "individual") {
      fetchStudents();
    }
  }, [recipientType, studentGradeFilter, studentSectionFilter, studentSearch]);

  const fetchQuota = async () => {
    try {
      const response = await fetch(`${API}/teacher/broadcast/quota`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setQuota(data.quota);
      }
    } catch (error) {
      console.error("Failed to fetch quota:", error);
    } finally {
      setQuotaLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API}/teacher/broadcast/classes`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      }
    } catch (error) {
      console.error("Failed to fetch classes:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/teacher/broadcast/templates`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API}/teacher/broadcast/history?limit=10`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.broadcasts || []);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const fetchSavedGroups = async () => {
    try {
      const response = await fetch(`${API}/teacher/broadcast/groups`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSavedGroups(data.groups || []);
      }
    } catch (error) {
      console.error("Failed to fetch saved groups:", error);
    }
  };

  const fetchStudents = useCallback(async () => {
    setStudentsLoading(true);
    try {
      const params = new URLSearchParams();
      if (studentGradeFilter) params.append("grade", studentGradeFilter);
      if (studentSectionFilter) params.append("section", studentSectionFilter);
      if (studentSearch) params.append("search", studentSearch);
      
      const response = await fetch(`${API}/teacher/broadcast/students?${params}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setAllStudents(data.students || []);
      }
    } catch (error) {
      console.error("Failed to fetch students:", error);
    } finally {
      setStudentsLoading(false);
    }
  }, [studentGradeFilter, studentSectionFilter, studentSearch]);

  const previewRecipients = async () => {
    if (recipientType === "class" && !selectedClass) {
      toast.error("Please select a class");
      return;
    }
    
    if (recipientType === "individual" && selectedStudents.length === 0) {
      toast.error("Please select at least one student");
      return;
    }

    setPreviewLoading(true);
    try {
      let requestBody = { recipient_type: recipientType };
      
      if (recipientType === "class") {
        const [grade, section] = selectedClass.split("-");
        requestBody.grade = parseInt(grade);
        requestBody.section = section || null;
      } else if (recipientType === "individual") {
        requestBody.student_ids = selectedStudents;
      } else if (recipientType === "custom_group") {
        if (selectedSavedGroup) {
          requestBody.saved_group_id = selectedSavedGroup;
        } else {
          requestBody.filter_criteria = {
            grades: filterGrades.length > 0 ? filterGrades : null,
            min_accuracy: filterMinAccuracy,
            max_accuracy: filterMaxAccuracy,
            inactive_days: filterInactiveDays
          };
        }
      }
      
      const response = await fetch(`${API}/teacher/broadcast/recipients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecipients(data);
      }
    } catch (error) {
      toast.error("Failed to load recipients");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setMessageContent(template.template);
    }
  };

  const handleSaveGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    try {
      const response = await fetch(`${API}/teacher/broadcast/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: newGroupName,
          description: newGroupDescription,
          filter_criteria: {
            grades: filterGrades.length > 0 ? filterGrades : null,
            min_accuracy: filterMinAccuracy,
            max_accuracy: filterMaxAccuracy,
            inactive_days: filterInactiveDays
          }
        })
      });

      if (response.ok) {
        toast.success("Group saved successfully");
        setShowSaveGroupDialog(false);
        setNewGroupName("");
        setNewGroupDescription("");
        fetchSavedGroups();
      } else {
        toast.error("Failed to save group");
      }
    } catch (error) {
      toast.error("Failed to save group");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      const response = await fetch(`${API}/teacher/broadcast/groups/${groupId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Group deleted");
        fetchSavedGroups();
        if (selectedSavedGroup === groupId) {
          setSelectedSavedGroup("");
        }
      }
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const handleSendBroadcast = async () => {
    if (!messageContent.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSending(true);
    try {
      let requestBody = {
        message_type: messageType,
        content: messageContent,
        template_name: selectedTemplate || null,
        media_url: mediaUrl || null,
        recipient_type: recipientType
      };

      if (recipientType === "class") {
        const [grade, section] = selectedClass.split("-");
        requestBody.grade = parseInt(grade);
        requestBody.section = section || null;
      } else if (recipientType === "individual") {
        requestBody.student_ids = selectedStudents;
      } else if (recipientType === "custom_group") {
        if (selectedSavedGroup) {
          requestBody.saved_group_id = selectedSavedGroup;
        } else {
          requestBody.filter_criteria = {
            grades: filterGrades.length > 0 ? filterGrades : null,
            min_accuracy: filterMinAccuracy,
            max_accuracy: filterMaxAccuracy,
            inactive_days: filterInactiveDays
          };
        }
      }
      
      const response = await fetch(`${API}/teacher/broadcast/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Broadcast sent to ${data.sent_count} recipients!`);
        
        if (data.quota_alert) {
          toast.warning(data.quota_alert.message);
        }
        
        // Reset form
        setMessageContent("");
        setSelectedTemplate("");
        setMediaUrl("");
        setRecipients(null);
        setSelectedStudents([]);
        setShowConfirmDialog(false);
        
        // Refresh data
        fetchQuota();
        fetchHistory();
      } else {
        toast.error(data.error || "Failed to send broadcast");
      }
    } catch (error) {
      toast.error("Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const selectAllStudents = () => {
    const eligibleStudents = allStudents.filter(s => s.has_phone).map(s => s.student_id);
    setSelectedStudents(eligibleStudents);
  };

  const clearAllStudents = () => {
    setSelectedStudents([]);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      completed: { color: "bg-green-100 text-green-700", icon: CheckCircle2 },
      partial: { color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
      failed: { color: "bg-red-100 text-red-700", icon: XCircle },
      sending: { color: "bg-blue-100 text-blue-700", icon: Loader2 },
      pending: { color: "bg-gray-100 text-gray-700", icon: Clock }
    };
    const config = statusMap[status] || statusMap.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const quotaPercent = quota ? Math.round((quota.used_this_month / quota.total_pool) * 100) : 0;

  // Get unique grades for filter
  const uniqueGrades = [...new Set(classes.map(c => c.grade))].sort((a, b) => a - b);
  const uniqueSections = [...new Set(classes.filter(c => c.section).map(c => c.section))].sort();

  // Loading state while checking feature flag
  if (flagLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Feature disabled view
  if (!isBroadcastEnabled) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="broadcast-locked-title">
              Broadcast Feature Unavailable
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              The WhatsApp Broadcast feature is currently disabled for your school. 
              Please contact your administrator if you believe this is an error.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/teacher/dashboard")}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Send className="w-6 h-6 text-emerald-600" />
            WhatsApp Broadcast
          </h1>
          <p className="text-gray-500 mt-1">
            Send messages to your students via WhatsApp
          </p>
        </div>
        
        {/* Quota Card */}
        {!quotaLoading && quota && (
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50 w-full sm:w-auto">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Monthly Quota</p>
                  <p className="text-lg font-bold text-gray-900">
                    <span className="text-emerald-600">{quota.remaining.toLocaleString()}</span>
                    <span className="text-gray-400 font-normal text-sm"> remaining of </span>
                    {quota.total_pool.toLocaleString()}
                  </p>
                  <div className="w-32 h-2 bg-gray-200 rounded-full mt-1">
                    <div 
                      className={`h-2 rounded-full ${
                        quotaPercent >= 90 ? 'bg-red-500' : 
                        quotaPercent >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(quotaPercent, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="compose" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* COMPOSE TAB */}
        <TabsContent value="compose" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Message Composer */}
            <div className="lg:col-span-2 space-y-6">
              {/* Message Type */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Message Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { id: "text", label: "Text", icon: MessageSquare, color: "blue" },
                      { id: "template", label: "Template", icon: FileText, color: "purple" },
                      { id: "image", label: "Image", icon: Image, color: "emerald" },
                      { id: "document", label: "Document", icon: FileText, color: "amber" }
                    ].map((type) => {
                      const Icon = type.icon;
                      const isActive = messageType === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setMessageType(type.id)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            isActive 
                              ? `border-${type.color}-500 bg-${type.color}-50` 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`message-type-${type.id}`}
                        >
                          <Icon className={`w-6 h-6 mx-auto mb-2 ${
                            isActive ? `text-${type.color}-600` : 'text-gray-400'
                          }`} />
                          <p className={`text-sm font-medium ${
                            isActive ? `text-${type.color}-700` : 'text-gray-600'
                          }`}>
                            {type.label}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Template Selection (if template type) */}
              {messageType === "template" && (
                <Card className="border-0 shadow-md">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">Select Template</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                      <SelectTrigger data-testid="template-select">
                        <SelectValue placeholder="Choose a template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              )}

              {/* Media URL (if image/document) */}
              {(messageType === "image" || messageType === "document") && (
                <Card className="border-0 shadow-md">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">
                      {messageType === "image" ? "Image URL" : "Document URL"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Input
                      placeholder={`Enter ${messageType} URL...`}
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      data-testid="media-url-input"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Provide a publicly accessible URL to the {messageType}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Message Content */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Message Content</CardTitle>
                  <CardDescription>
                    {messageType === "template" 
                      ? "Customize the template message below"
                      : "Write your message to students"
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Type your message here..."
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    rows={6}
                    className="resize-none"
                    data-testid="message-content"
                  />
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>{messageContent.length} characters</span>
                    <span>Max 1024 characters for body text</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Recipients */}
            <div className="space-y-6">
              {/* Recipient Selection */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Recipients
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Recipient Type */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: "class", label: "Class", icon: Users },
                      { id: "individual", label: "Individual", icon: User },
                      { id: "custom_group", label: "Custom", icon: Filter }
                    ].map((type) => {
                      const Icon = type.icon;
                      const isActive = recipientType === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => {
                            setRecipientType(type.id);
                            setRecipients(null);
                            setSelectedStudents([]);
                          }}
                          className={`p-3 rounded-lg border transition-all ${
                            isActive 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`recipient-type-${type.id}`}
                        >
                          <Icon className={`w-5 h-5 mx-auto mb-1 ${
                            isActive ? 'text-blue-600' : 'text-gray-400'
                          }`} />
                          <p className={`text-xs font-medium ${
                            isActive ? 'text-blue-700' : 'text-gray-600'
                          }`}>
                            {type.label}
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  {/* Class Selection */}
                  {recipientType === "class" && (
                    <div className="space-y-3">
                      <Label>Select Class</Label>
                      <Select value={selectedClass} onValueChange={setSelectedClass}>
                        <SelectTrigger data-testid="class-select">
                          <SelectValue placeholder="Choose a class..." />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map((cls) => (
                            <SelectItem 
                              key={`${cls.grade}-${cls.section || ''}`} 
                              value={`${cls.grade}-${cls.section || ''}`}
                            >
                              {cls.label} ({cls.student_count} students)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={previewRecipients}
                        disabled={!selectedClass || previewLoading}
                        data-testid="preview-recipients-btn"
                      >
                        {previewLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Users className="w-4 h-4 mr-2" />
                        )}
                        Preview Recipients
                      </Button>
                    </div>
                  )}

                  {/* Individual Selection */}
                  {recipientType === "individual" && (
                    <div className="space-y-3">
                      {/* Search and filters */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search students..."
                          value={studentSearch}
                          onChange={(e) => setStudentSearch(e.target.value)}
                          className="pl-9"
                          data-testid="student-search"
                        />
                      </div>

                      <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="w-full justify-between">
                            <span className="flex items-center gap-2">
                              <Filter className="w-4 h-4" />
                              Filters
                            </span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-2 pt-2">
                          <Select value={studentGradeFilter || "__all__"} onValueChange={(v) => setStudentGradeFilter(v === "__all__" ? "" : v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="All Grades" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All Grades</SelectItem>
                              {uniqueGrades.map(g => (
                                <SelectItem key={g} value={String(g)}>Grade {g}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={studentSectionFilter || "__all__"} onValueChange={(v) => setStudentSectionFilter(v === "__all__" ? "" : v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="All Sections" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All Sections</SelectItem>
                              {uniqueSections.map(s => (
                                <SelectItem key={s} value={s}>Section {s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Selection actions */}
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={selectAllStudents}
                          className="flex-1"
                        >
                          Select All
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={clearAllStudents}
                          className="flex-1"
                        >
                          Clear
                        </Button>
                      </div>

                      {/* Selected count */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Selected:</span>
                        <Badge variant="secondary">{selectedStudents.length} students</Badge>
                      </div>

                      {/* Student list */}
                      <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
                        {studentsLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                          </div>
                        ) : allStudents.length === 0 ? (
                          <p className="text-center py-8 text-gray-500 text-sm">No students found</p>
                        ) : (
                          allStudents.map((student) => (
                            <label
                              key={student.student_id}
                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                selectedStudents.includes(student.student_id)
                                  ? 'bg-blue-50'
                                  : 'hover:bg-gray-50'
                              } ${!student.has_phone ? 'opacity-50' : ''}`}
                            >
                              <Checkbox
                                checked={selectedStudents.includes(student.student_id)}
                                onCheckedChange={() => toggleStudentSelection(student.student_id)}
                                disabled={!student.has_phone}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {student.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Grade {student.grade}{student.section ? `-${student.section}` : ''}
                                  {student.accuracy !== null && (
                                    <span className="ml-2">
                                      {student.accuracy}% accuracy
                                    </span>
                                  )}
                                </p>
                              </div>
                              {!student.has_phone && (
                                <Badge variant="outline" className="text-xs">No phone</Badge>
                              )}
                            </label>
                          ))
                        )}
                      </div>

                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={previewRecipients}
                        disabled={selectedStudents.length === 0 || previewLoading}
                        data-testid="preview-individual-btn"
                      >
                        {previewLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Users className="w-4 h-4 mr-2" />
                        )}
                        Preview ({selectedStudents.length})
                      </Button>
                    </div>
                  )}

                  {/* Custom Group Selection */}
                  {recipientType === "custom_group" && (
                    <div className="space-y-4">
                      {/* Saved Groups */}
                      {savedGroups.length > 0 && (
                        <div className="space-y-2">
                          <Label>Saved Groups</Label>
                          <Select value={selectedSavedGroup || "__new__"} onValueChange={(v) => {
                            const newValue = v === "__new__" ? "" : v;
                            setSelectedSavedGroup(newValue);
                            if (newValue) {
                              // Clear custom filters when selecting saved group
                              setFilterGrades([]);
                              setFilterMinAccuracy(null);
                              setFilterMaxAccuracy(null);
                              setFilterInactiveDays(null);
                            }
                          }}>
                            <SelectTrigger data-testid="saved-group-select">
                              <SelectValue placeholder="Choose a saved group or create new..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__new__">Create New Filter</SelectItem>
                              {savedGroups.map((group) => (
                                <SelectItem key={group.id} value={group.id}>
                                  <div className="flex items-center justify-between w-full">
                                    <span>{group.name}</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {group.recipient_count}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {selectedSavedGroup && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteGroup(selectedSavedGroup)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Group
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Custom Filters (when not using saved group) */}
                      {!selectedSavedGroup && (
                        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm text-gray-700">Filter Students</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowSaveGroupDialog(true)}
                              disabled={!filterGrades.length && !filterMinAccuracy && !filterMaxAccuracy && !filterInactiveDays}
                            >
                              <Save className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                          </div>

                          {/* Grade Filter */}
                          <div className="space-y-2">
                            <Label className="text-xs text-gray-600">Grades</Label>
                            <div className="flex flex-wrap gap-2">
                              {uniqueGrades.map(g => (
                                <button
                                  key={g}
                                  onClick={() => {
                                    setFilterGrades(prev => 
                                      prev.includes(g) 
                                        ? prev.filter(x => x !== g)
                                        : [...prev, g]
                                    );
                                  }}
                                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                    filterGrades.includes(g)
                                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  Grade {g}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Performance Filters */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <TrendingDown className="w-4 h-4 text-red-500" />
                              <Label className="text-xs text-gray-600">Struggling Students</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Accuracy below</span>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                placeholder="50"
                                className="w-20 h-8"
                                value={filterMaxAccuracy || ""}
                                onChange={(e) => setFilterMaxAccuracy(e.target.value ? parseInt(e.target.value) : null)}
                              />
                              <span className="text-xs text-gray-500">%</span>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-green-500" />
                              <Label className="text-xs text-gray-600">High Performers</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Accuracy above</span>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                placeholder="80"
                                className="w-20 h-8"
                                value={filterMinAccuracy || ""}
                                onChange={(e) => setFilterMinAccuracy(e.target.value ? parseInt(e.target.value) : null)}
                              />
                              <span className="text-xs text-gray-500">%</span>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <UserX className="w-4 h-4 text-amber-500" />
                              <Label className="text-xs text-gray-600">Inactive Students</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Inactive for</span>
                              <Input
                                type="number"
                                min="1"
                                placeholder="7"
                                className="w-20 h-8"
                                value={filterInactiveDays || ""}
                                onChange={(e) => setFilterInactiveDays(e.target.value ? parseInt(e.target.value) : null)}
                              />
                              <span className="text-xs text-gray-500">+ days</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={previewRecipients}
                        disabled={previewLoading}
                        data-testid="preview-custom-btn"
                      >
                        {previewLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Users className="w-4 h-4 mr-2" />
                        )}
                        Preview Recipients
                      </Button>
                    </div>
                  )}

                  {/* Recipients Preview */}
                  {recipients && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Recipients Found
                        </span>
                        <Badge variant="secondary">
                          {recipients.recipient_count} students
                        </Badge>
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {recipients.recipients.map((r, i) => (
                          <div 
                            key={i}
                            className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm"
                          >
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-600">
                                {r.name?.charAt(0) || "?"}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{r.name}</p>
                              <p className="text-xs text-gray-500">
                                Grade {r.grade}{r.section ? `-${r.section}` : ''}
                                {r.accuracy !== null && r.accuracy !== undefined && (
                                  <span className={`ml-2 ${r.accuracy < 50 ? 'text-red-500' : 'text-green-500'}`}>
                                    {r.accuracy}%
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                        {recipients.has_more && (
                          <p className="text-xs text-gray-500 text-center py-2">
                            +{recipients.recipient_count - recipients.recipients.length} more students
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Send Button */}
              <Button 
                className="w-full h-12 text-lg bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowConfirmDialog(true)}
                disabled={
                  !messageContent.trim() || 
                  sending ||
                  (recipientType === "class" && !selectedClass) ||
                  (recipientType === "individual" && selectedStudents.length === 0)
                }
                data-testid="send-broadcast-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                Send Broadcast
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="space-y-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-gray-600" />
                Recent Broadcasts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Send className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No broadcasts sent yet</p>
                  <p className="text-sm">Your broadcast history will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map((broadcast) => (
                    <div 
                      key={broadcast.id}
                      className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        broadcast.message_type === 'text' ? 'bg-blue-100' :
                        broadcast.message_type === 'template' ? 'bg-purple-100' :
                        broadcast.message_type === 'image' ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}>
                        {broadcast.message_type === 'text' && <MessageSquare className="w-5 h-5 text-blue-600" />}
                        {broadcast.message_type === 'template' && <FileText className="w-5 h-5 text-purple-600" />}
                        {broadcast.message_type === 'image' && <Image className="w-5 h-5 text-emerald-600" />}
                        {broadcast.message_type === 'document' && <FileText className="w-5 h-5 text-amber-600" />}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(broadcast.status)}
                          <span className="text-xs text-gray-500">
                            {new Date(broadcast.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 line-clamp-2">
                          {broadcast.content}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {broadcast.recipient_count} recipients
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            {broadcast.sent_count} sent
                          </span>
                          {broadcast.failed_count > 0 && (
                            <span className="flex items-center gap-1">
                              <XCircle className="w-3 h-3 text-red-500" />
                              {broadcast.failed_count} failed
                            </span>
                          )}
                        </div>
                      </div>

                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Broadcast</DialogTitle>
            <DialogDescription>
              You are about to send a WhatsApp message to {recipients?.recipient_count || selectedStudents.length || 'selected'} students.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Message Preview:</p>
              <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-4">
                {messageContent}
              </p>
            </div>
            
            {quota && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Quota remaining after send:</span>
                <span className={`font-medium ${
                  quota.remaining - (recipients?.recipient_count || selectedStudents.length || 0) < 0 
                    ? 'text-red-600' 
                    : 'text-green-600'
                }`}>
                  {Math.max(0, quota.remaining - (recipients?.recipient_count || selectedStudents.length || 0))} messages
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendBroadcast}
              disabled={sending}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-send-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Group Dialog */}
      <Dialog open={showSaveGroupDialog} onOpenChange={setShowSaveGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-blue-600" />
              Save Custom Group
            </DialogTitle>
            <DialogDescription>
              Save this filter as a reusable group for quick access later.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input
                placeholder="e.g., Struggling Grade 6 Students"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                data-testid="group-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="Describe this group..."
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-600 mb-2">Current Filters:</p>
              <div className="flex flex-wrap gap-2">
                {filterGrades.length > 0 && (
                  <Badge variant="secondary">Grades: {filterGrades.join(', ')}</Badge>
                )}
                {filterMaxAccuracy && (
                  <Badge variant="secondary">Accuracy &lt; {filterMaxAccuracy}%</Badge>
                )}
                {filterMinAccuracy && (
                  <Badge variant="secondary">Accuracy &gt; {filterMinAccuracy}%</Badge>
                )}
                {filterInactiveDays && (
                  <Badge variant="secondary">Inactive {filterInactiveDays}+ days</Badge>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveGroupDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup} data-testid="save-group-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
